import type { SurfaceHandlingTable, TreeFellingConfig } from './tuning.js';

/**
 * Tuning for the 2-wheel motorcycle. Shape parallels the car's VehicleTuning where it
 * matters; the values are picked to feel like a light, flickable bike:
 *   - half the chassis dimensions, ~1/5 the mass
 *   - front wheel steers + drives, rear wheel drives only
 *   - softer / longer suspension travel — bikes ride lower and squat more
 *   - quicker steering, narrower lock angle (real bikes lean, but we don't simulate that;
 *     fast small-angle steering is a passable substitute for arcade purposes)
 *   - lower engine + brake force than the car, but mass scales down faster than force,
 *     so accel and stopping feel comparable
 *   - stricter tree-felling threshold — a 250 kg bike should not bulldoze trees
 */
export const MotorcycleTuning = {
  chassisWidth: 0.5,
  chassisHeight: 1.1,
  chassisLength: 2.1,
  chassisMass: 250,

  wheelRadius: 0.32,
  wheelWidth: 0.18,
  /** Bike wheels are inline (x = 0). wheelTrack is unused for the physics wheels but
   *  kept in the table so callers reading the same key shape don't blow up. */
  wheelTrack: 0,
  frontAxle: 0.85,
  rearAxle: 0.85,
  wheelAnchorY: -0.25,

  suspensionRestLength: 0.4,
  suspensionTravel: 0.35,
  suspensionStiffness: 55,
  suspensionCompression: 1.4,
  suspensionRelaxation: 1.2,
  maxSuspensionForce: 18000,

  frictionSlip: 4.5,
  sideFrictionStiffness: 1.4,

  /** Per driven wheel. With 2 driven wheels and 250 kg, 1400 N each ≈ 11 m/s² peak. */
  engineForce: 1400,
  /** CoM impulse brake — bike is light, so less force gives similar deceleration. */
  brakeForce: 4500,
  handbrakeForce: 2000,
  brakeRampRate: 12,
  brakeReverseBlendSpeed: 1.5,

  /** Lower than the car: visual chassis is taller-but-narrower, but to avoid tipping we
   *  still want the CoM well below the seat box. */
  centerOfMassY: -0.4,

  maxSteer: 0.65,
  steerSpeedReference: 25,
  minSteerAtHighSpeed: 0.22,
  steerRate: 5.0,

  treeFelling: {
    fellMinSpeedKmh: 25,
    fellLossAtMinKmh: 15,
    fellLossZeroAtKmh: 70,
    fallSeconds: 1.2,
    colliderRadius: 0.4,
    cullRadius: 80,
  } satisfies TreeFellingConfig,

  linearDamping: 0.05,
  angularDamping: 2.0,

  /**
   * Arcade upright-keeper. The bike has two inline wheels (x=0), so it has zero passive
   * lateral support — any tilt at all and gravity will lay it on its side. Worse, once
   * laid down, the wheel suspension raycasts shoot sideways, produce garbage forces, and
   * Rapier eventually integrates NaN into the chassis state (the "blue world / NaN speed /
   * unreachable wasm" crash you may have seen before this tuning existed).
   *
   * Solution: every substep, apply a PD torque about the horizontal axes that drives the
   * chassis's local +Y back toward world +Y. `stiffness` is the spring constant on the
   * tilt angle (sin-shaped — auto-saturates so we can't over-correct); `damping` opposes
   * angular velocity about the same axes so we don't oscillate or spin. Yaw (Y axis) is
   * untouched — turning still works normally.
   */
  upright: {
    stiffness: 15000,
    damping: 2500,
  },

  /** Bikes break loose on grass more aggressively (lighter rear) but recover quickly. */
  surfaceHandling: {
    road:  { throttleMul: 1.0,  extraLinearDamping: 0.0,  surfaceGripFloor: 1.0,  gripStressMul: 0.2, maxYawRateRadS: 2.8, extraAngularDamping: 0.0, slipDragMul: 0.3 },
    path:  { throttleMul: 0.8,  extraLinearDamping: 0.0,  surfaceGripFloor: 0.55, gripStressMul: 0.9, maxYawRateRadS: 1.8, extraAngularDamping: 0.3, slipDragMul: 0.7 },
    grass: { throttleMul: 0.55, extraLinearDamping: 0.0,  surfaceGripFloor: 0.3,  gripStressMul: 1.2, maxYawRateRadS: 1.4, extraAngularDamping: 0.6, slipDragMul: 1.1 },
  } satisfies SurfaceHandlingTable,

  gripLossRate: 2.5,
  gripRecoverRate: 1.0,
  gripStress: {
    speedFull: 60,
    speedExponent: 1.5,
    lateralGFull: 11,
    accelGFull: 14,
    steerRateFull: 5,
    derivativeSmoothing: 0.05,
  },

  slipDrag: {
    coefficient: 35,
  },
} as const;
